import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

// Pages
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Providers from './pages/Providers';
import BotActions from './pages/BotActions';
import Jobs from './pages/Jobs';
import Payments from './pages/Payments';
import Referrals from './pages/Referrals';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import BillingDashboard from './pages/BillingDashboard';
import BillingBalances from './pages/BillingBalances';
import BillingPlans from './pages/BillingPlans';
import BillingPackages from './pages/BillingPackages';
import BillingSubscriptions from './pages/BillingSubscriptions';
import Login from './pages/Login';
import AdminUsers from './pages/AdminUsers';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { adminAPI } from './services/api';

// Theme
const theme = {
  colors: {
    primary: '#000000',
    secondary: '#1f2937',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    background: '#f8fafc',
    surface: '#ffffff',
    text: '#1f2937',
    textSecondary: '#6b7280',
    border: '#e5e7eb'
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem'
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
  }
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
`;

const AppContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 250px;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.lg};
`;

const LoadingState = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: ${props => props.theme.colors.text};
`;

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const getInitialAuthState = () => {
  const storedToken = localStorage.getItem('admin_token');
  const storedProfileRaw = localStorage.getItem('admin_profile');
  const hasProfileValue =
    storedProfileRaw &&
    storedProfileRaw !== 'undefined' &&
    storedProfileRaw !== 'null' &&
    storedProfileRaw.trim() !== '';

  if (storedToken && hasProfileValue) {
    try {
      const parsed = JSON.parse(storedProfileRaw);
      return { token: storedToken, profile: parsed };
    } catch (error) {
      console.warn('Failed to parse admin profile from storage, clearing.', error);
      localStorage.removeItem('admin_profile');
    }
  } else if (!storedToken && storedProfileRaw) {
    localStorage.removeItem('admin_profile');
  }

  return { token: null, profile: null };
};

function App() {
  const initialAuth = getInitialAuthState();
  const [token, setToken] = useState(initialAuth.token);
  const [adminProfile, setAdminProfile] = useState(initialAuth.profile);
  const [checkingSession, setCheckingSession] = useState(!!token);
  const skipProfileFetchRef = React.useRef(false);

  const handleLogout = useCallback(async () => {
    try {
      await adminAPI.logout();
    } catch (error) {
      console.warn('Logout request failed (ignored)', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_profile');
      setToken(null);
      setAdminProfile(null);
      setCheckingSession(false);
    }
  }, []);

  const handleProfileRefresh = useCallback((nextProfile) => {
    if (nextProfile) {
      setAdminProfile(nextProfile);
      localStorage.setItem('admin_profile', JSON.stringify(nextProfile));
    } else {
      setAdminProfile(null);
      localStorage.removeItem('admin_profile');
    }
  }, []);

  useEffect(() => {
    if (!token) {
      localStorage.removeItem('admin_profile');
      setCheckingSession(false);
      setAdminProfile(null);
      return;
    }

    if (skipProfileFetchRef.current) {
      skipProfileFetchRef.current = false;
      setCheckingSession(false);
      return;
    }

    // Если профиль уже есть (только что залогинились) —
    // не делаем лишний запрос /admin/auth/me, он понадобится только после reload.
    if (adminProfile) {
      setCheckingSession(false);
      return;
    }
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        const { data } = await adminAPI.getProfile();
        if (!isMounted) return;
        handleProfileRefresh(data);
      } catch (error) {
        console.error('Failed to restore admin session', error);
        await handleLogout();
      } finally {
        if (isMounted) {
          setCheckingSession(false);
        }
      }
    };
    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, [token, adminProfile, handleLogout, handleProfileRefresh]);

  const handleLoginSuccess = (newToken, profile) => {
    skipProfileFetchRef.current = true;
    localStorage.setItem('admin_token', newToken);
    setToken(newToken);
    handleProfileRefresh(profile);
    setCheckingSession(false);
  };

  const renderContent = () => {
    if (checkingSession) {
      return <LoadingState>Проверяем сессию...</LoadingState>;
    }

    if (!token || !adminProfile) {
      return <Login onSuccess={handleLoginSuccess} />;
    }

    return (
      <AppContainer>
        <Sidebar onLogout={handleLogout} />
        <MainContent>
          <Header admin={adminProfile} />
          <ContentArea>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/users" element={<Users />} />
              <Route path="/providers" element={<Providers />} />
              <Route path="/bot-actions" element={<BotActions />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/billing" element={<BillingDashboard />} />
              <Route path="/billing/balances" element={<BillingBalances />} />
              <Route path="/billing/plans" element={<BillingPlans />} />
              <Route path="/billing/packages" element={<BillingPackages />} />
              <Route path="/billing/subscriptions" element={<BillingSubscriptions />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/settings" element={<Settings />} />
              <Route
                path="/admin-users"
                element={
                  <AdminUsers
                    currentAdmin={adminProfile}
                    onCurrentAdminUpdate={handleProfileRefresh}
                  />
                }
              />
            </Routes>
          </ContentArea>
        </MainContent>
      </AppContainer>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <QueryClientProvider client={queryClient}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          {renderContent()}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
